import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useRouter } from 'next/router';
import getSingleQueryParam from '@discover-lib/getSingleQueryParam';
import { captureSessionCampaignAttribution } from '@/lib/analytics/amplitude/sessionCampaignAttribution';
import logger from './logger';

export type SignupAttribution = {
  SignupSource?: string;
  SignupMedium?: string;
  SignupCampaign?: string;
};

const SignupAttributionContext = createContext<SignupAttribution>({});

type SignupAttributionProviderProps = {
  children: ReactNode;
};

const hasAttributionValues = (attribution: SignupAttribution): boolean =>
  Boolean(attribution.SignupSource || attribution.SignupMedium || attribution.SignupCampaign);

const isSameAttribution = (a: SignupAttribution, b: SignupAttribution): boolean =>
  a.SignupSource === b.SignupSource &&
  a.SignupMedium === b.SignupMedium &&
  a.SignupCampaign === b.SignupCampaign;

// In-memory only (no cookie / localStorage / sessionStorage): last-touch UTMs for signup.
// Survives SPA navigation and OAuth popups; lost on hard refresh by design.
export const SignupAttributionProvider = ({ children }: SignupAttributionProviderProps) => {
  const router = useRouter();
  const [attribution, setAttribution] = useState<SignupAttribution>({});

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const source = getSingleQueryParam(router.query.utm_source);
    const medium = getSingleQueryParam(router.query.utm_medium);
    const campaign = getSingleQueryParam(router.query.utm_campaign);

    if (!source && !medium && !campaign) {
      return;
    }

    const nextAttribution: SignupAttribution = {
      ...(source ? { SignupSource: source } : {}),
      ...(medium ? { SignupMedium: medium } : {}),
      ...(campaign ? { SignupCampaign: campaign } : {}),
    };

    captureSessionCampaignAttribution({ source, medium, campaign });
    logger.debug('captured last-touch attribution', nextAttribution);
    setAttribution((prev) => (isSameAttribution(prev, nextAttribution) ? prev : nextAttribution));
  }, [router.isReady, router.query.utm_source, router.query.utm_medium, router.query.utm_campaign]);

  return (
    <SignupAttributionContext.Provider value={attribution}>
      {children}
    </SignupAttributionContext.Provider>
  );
};

export const useSignupAttribution = (): SignupAttribution => useContext(SignupAttributionContext);

type ApplySignupAttributionOptions = {
  surface: string;
  /** Also log when no UTMs were captured. Use on dedicated signup surfaces. */
  logWhenEmpty?: boolean;
};

/** Merge captured attribution into mutation variables and emit a debug log when enabled. */
export function applySignupAttribution<T extends Record<string, unknown>>(
  variables: T,
  attribution: SignupAttribution | null | undefined,
  { surface, logWhenEmpty = false }: ApplySignupAttributionOptions
): T & SignupAttribution {
  const attrs = attribution ?? {};
  const hasAttribution = hasAttributionValues(attrs);

  if (hasAttribution || logWhenEmpty) {
    logger.debug(`${surface} mutation attribution`, hasAttribution ? attrs : '(none)');
  }

  return { ...variables, ...attrs };
}
